import {Spin} from '@superweb/ui'
import clsx from 'clsx'
import React, {useState} from 'react'

import {MapsContextProvider, useMapContextValue} from '../../../context'
import type {MapProps as MapPropsBase, MapRef} from '../../../model'
import {useCustomization} from '../lib'
import styleClasses from './classes.module.css'
import {MapMargin} from './MapMargin'

type MapProps = MapPropsBase &
  React.PropsWithChildren<{
    errorMessage?: string
    lang: string
    renderFallback?(): React.ReactElement
    theme?: 'light' | 'dark'
    brand: 'yango' | 'yandex'
  }>

type MapProvidedProps = MapProps & {
  mapRef?: MapRef | null
  setMapRef: (ref: MapRef | null | undefined) => void
}

const defaultRenderFallback = () => (
  <div className={styleClasses.spinner}>
    <Spin size="xs" />
  </div>
)

const MapProvided: React.FC<MapProvidedProps> = ({
  children,
  renderFallback = defaultRenderFallback,
  theme = 'light',
  setMapRef,
  className,
  ...props
}) => {
  const mapContext = useMapContextValue()
  const customization = useCustomization(theme)

  if (!mapContext || !customization) {
    return renderFallback()
  }

  const {
    components: {YMap, YMapDefaultFeaturesLayer, YMapDefaultSchemeLayer},
  } = mapContext

  return (
    <YMap ref={setMapRef} className={clsx(styleClasses.root, className)} copyrights={false} mode="vector" {...props}>
      <MapMargin margin={props.margin} />
      <YMapDefaultSchemeLayer customization={customization} />
      <YMapDefaultFeaturesLayer />
      {children}
    </YMap>
  )
}

export const Map: React.FC<MapProps> = ({errorMessage, brand, ...props}) => {
  const [mapRef, setMapRef] = useState<MapRef | null>()
  return (
    <MapsContextProvider
      brand={brand}
      errorMessage={errorMessage}
      lang={props.lang}
      mapRef={mapRef}
      setMapRef={setMapRef}
    >
      <MapProvided brand={brand} mapRef={mapRef} setMapRef={setMapRef} {...props} />
    </MapsContextProvider>
  )
}
